/** Serverzustand der Wissensdatenbank. */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createArticle,
  createArticleFromTicket,
  createKbCategory,
  deleteArticle,
  deleteKbCategory,
  getArticle,
  getArticleVersion,
  listArticleAttachments,
  listArticleVersions,
  listKbCategories,
  rateArticle,
  removeArticleAttachment,
  remindKbReviews,
  runArticleAction,
  searchArticles,
  suggestArticles,
  updateArticle,
  uploadArticleAttachment,
  type ArticleAction,
  type ArticleFilters,
} from './api';

export const knowledgeKeys = {
  all: ['knowledge'] as const,
  search: (filters: ArticleFilters) => ['knowledge', 'search', filters] as const,
  suggestions: (text: string) => ['knowledge', 'suggestions', text] as const,
  article: (articleId: string) => ['knowledge', 'article', articleId] as const,
  versions: (articleId: string) => ['knowledge', 'versions', articleId] as const,
  attachments: (articleId: string) => ['knowledge', 'attachments', articleId] as const,
  // Eine alte Fassung und ihre Dateien stehen in einem eigenen Zweig und
  // nicht unter ``versions``/``attachments``. Zwei Gruende: Sie aendern sich
  // nie -- eine Fassung ist unveraenderlich --, und die bestehenden
  // Ungueltigkeitserklaerungen nach jedem Schreibvorgang wuerfen sie sonst
  // weg, um denselben Stand noch einmal zu holen.
  version: (articleId: string, versionId: string) =>
    ['knowledge', 'version', articleId, versionId] as const,
  versionAttachments: (articleId: string, versionId: string) =>
    ['knowledge', 'version-attachments', articleId, versionId] as const,
  categories: ['knowledge', 'categories'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/**
 * Die Trefferliste, seitenweise nachladbar -- F4-27.
 *
 * Als Infinite Query und nicht als eigene Seitenzaehlung: Der Cursor kommt
 * vom Server und ist undurchsichtig; ihn im Zustand der Seite mitzufuehren
 * hiesse, die Bedingung des ersten Aufrufs ein zweites Mal zu verwalten.
 *
 * **Der Filterzustand steckt im Schluessel.** Aendert sich die Suche oder ein
 * Filter, faengt die Abfrage von vorn an -- eine nachgeladene Seite aus einer
 * alten Bedingung waere sonst unter die neue Liste gerutscht.
 */
export function useArticleSearch(filters: ArticleFilters) {
  return useInfiniteQuery({
    queryKey: knowledgeKeys.search(filters),
    queryFn: ({ pageParam }) => searchArticles(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Beim Tippen bleibt die alte Trefferliste stehen, statt zu flackern.
    placeholderData: keepPreviousData,
    retry: retryUnlessPermanent,
  });
}

/**
 * Vorschlaege zum Betreff -- F4-13.
 *
 * Erst ab drei Zeichen, sonst waere jeder Tastendruck eine Volltextsuche. Die
 * Antwort bleibt fuenf Minuten frisch: Wer im Formular hin und her springt,
 * soll den Server nicht erneut fragen.
 */
export function useArticleSuggestions(text: string) {
  const trimmed = text.trim();
  return useQuery({
    queryKey: knowledgeKeys.suggestions(trimmed),
    queryFn: () => suggestArticles(trimmed),
    enabled: trimmed.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useArticle(articleId: string | null) {
  const id = articleId ?? '';
  return useQuery({
    queryKey: knowledgeKeys.article(id),
    queryFn: () => getArticle(id),
    enabled: articleId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useArticleVersions(articleId: string | null) {
  const id = articleId ?? '';
  return useQuery({
    queryKey: knowledgeKeys.versions(id),
    queryFn: () => listArticleVersions(id),
    enabled: articleId !== null,
    retry: retryUnlessPermanent,
  });
}

/**
 * Eine alte Fassung mit ihrem Inhalt -- F4-34.
 *
 * ``staleTime: Infinity``: Eine Fassung wird nie geaendert. Sie erneut zu
 * holen, weil ein Fenster den Fokus bekommt, brachte garantiert dieselben
 * Bytes zurueck.
 */
export function useArticleVersion(articleId: string, versionId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.version(articleId, versionId ?? ''),
    queryFn: () => getArticleVersion(articleId, versionId ?? ''),
    enabled: versionId !== null,
    staleTime: Infinity,
    retry: retryUnlessPermanent,
  });
}

/** Die Dateien, die an einer alten Fassung hingen -- ebenfalls unveraenderlich. */
export function useVersionAttachments(articleId: string, versionId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.versionAttachments(articleId, versionId ?? ''),
    queryFn: () => listArticleAttachments(articleId, versionId ?? ''),
    enabled: versionId !== null,
    staleTime: Infinity,
    retry: retryUnlessPermanent,
  });
}

export function useKbCategories() {
  return useQuery({
    queryKey: knowledgeKeys.categories,
    queryFn: listKbCategories,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Nach jeder Aenderung wird der ganze Zweig entwertet.
 *
 * Eine Freigabe aendert den Artikel, seine Stellung in jeder Trefferliste und
 * -- ueber die Sichtbarkeit -- auch, wer ihn ueberhaupt findet. Gezielt zu
 * entwerten hiesse, diese Zusammenhaenge in der Oberflaeche nachzubauen.
 */
function useKnowledgeMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: knowledgeKeys.all });
    },
  });
}

export function useCreateArticle() {
  return useKnowledgeMutation((payload: Record<string, unknown>) => createArticle(payload));
}

export function useUpdateArticle() {
  return useKnowledgeMutation((variables: { id: string; payload: Record<string, unknown> }) =>
    updateArticle(variables.id, variables.payload),
  );
}

export function useDeleteArticle() {
  return useKnowledgeMutation((articleId: string) => deleteArticle(articleId));
}

export function useArticleAction() {
  return useKnowledgeMutation((variables: { id: string; action: ArticleAction }) =>
    runArticleAction(variables.id, variables.action),
  );
}

export function useArticleFromTicket() {
  return useKnowledgeMutation((payload: Record<string, unknown>) =>
    createArticleFromTicket(payload),
  );
}

export function useRateArticle() {
  return useKnowledgeMutation((variables: { id: string; rating: number }) =>
    rateArticle(variables.id, variables.rating),
  );
}

export function useCreateKbCategory() {
  return useKnowledgeMutation((payload: Record<string, unknown>) => createKbCategory(payload));
}

export function useDeleteKbCategory() {
  return useKnowledgeMutation((categoryId: string) => deleteKbCategory(categoryId));
}

/** Der Erinnerungslauf von Hand -- F4-16. */
export function useRemindReviews() {
  return useMutation({ mutationFn: remindKbReviews });
}

export function useArticleAttachments(articleId: string | null) {
  return useQuery({
    queryKey: knowledgeKeys.attachments(articleId ?? ''),
    queryFn: () => listArticleAttachments(articleId ?? ''),
    enabled: articleId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useUploadArticleAttachment(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadArticleAttachment(articleId, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: knowledgeKeys.attachments(articleId) });
    },
  });
}

/**
 * Einen Anhang entfernen -- und das ist ein Schreibvorgang am Artikel.
 *
 * Es entsteht eine neue Fassung (F4-24). Deshalb wird nicht nur die
 * Anhangsliste ungueltig, sondern auch der Artikel und seine Fassungsliste --
 * sonst stuende dort weiter die alte Fassungsnummer.
 */
export function useRemoveArticleAttachment(articleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeArticleAttachment(articleId, attachmentId),
    onSuccess: async (article) => {
      queryClient.setQueryData(knowledgeKeys.article(articleId), article);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.attachments(articleId) }),
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.versions(articleId) }),
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.all }),
      ]);
    },
  });
}
